import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { VideoSubmission } from '../../lib/supabase';
import { format } from 'date-fns';
import AdminLayout from '../../components/feature/AdminLayout';

export default function AdminVideos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState<VideoSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoSubmission | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [editViewCount, setEditViewCount] = useState('');
  const [editRewardAmount, setEditRewardAmount] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const ADMIN_EMAILS = ['yirun0206@gmail.com'];

  // -------------------------------------------------------------------------
  // Auth & initial data load
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (!ADMIN_EMAILS.includes(user.email || '')) {
      navigate('/');
      return;
    }
    loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Auto‑dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // -------------------------------------------------------------------------
  // Data helpers
  // -------------------------------------------------------------------------
  const loadVideos = async () => {
    try {
      const { data } = await supabase
        .from('video_submissions')
        .select('*')
        .order('created_at', { ascending: false });
      if (data) setVideos(data);
    } catch (error) {
      console.error('Error loading videos:', error);
      setToast({ message: '載入影片失敗', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Update helpers
  // -------------------------------------------------------------------------
  const updateVideoStatus = async (
    id: string,
    status: 'approved' | 'rejected',
    notes?: string
  ) => {
    setUpdating(id);
    try {
      const updateData: Record<string, unknown> = {
        status,
        admin_notes: notes || null,
        updated_at: new Date().toISOString(),
      };

      // If approved and a reward amount is set, include it
      if (status === 'approved' && editRewardAmount) {
        updateData.reward_amount = parseInt(editRewardAmount, 10) || 0;
      }

      const { error } = await supabase
        .from('video_submissions')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // When approved, add reward to creator profile
      if (status === 'approved') {
        const video = videos.find(v => v.id === id);
        if (video?.creator_id) {
          const rewardToAdd = parseInt(editRewardAmount, 10) || 0;
          if (rewardToAdd > 0) {
            const { data: profile } = await supabase
              .from('creator_profiles')
              .select('total_rewards')
              .eq('id', video.creator_id)
              .maybeSingle();

            if (profile) {
              await supabase
                .from('creator_profiles')
                .update({
                  total_rewards: (profile.total_rewards || 0) + rewardToAdd,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', video.creator_id);
            }
          }
        }
      }

      await loadVideos();
      resetEditState();
      setToast({ message: status === 'approved' ? '已通過審核' : '已拒絕審核', type: 'success' });
    } catch (error) {
      console.error('Error updating video status:', error);
      setToast({ message: '操作失敗，請重試', type: 'error' });
    } finally {
      setUpdating(null);
    }
  };

  const updateVideoData = async (id: string) => {
    setUpdating(id);
    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (editViewCount !== '') {
        updateData.view_count = parseInt(editViewCount, 10) || 0;
      }
      if (editRewardAmount !== '') {
        updateData.reward_amount = parseInt(editRewardAmount, 10) || 0;
      }
      if (adminNotes !== (selectedVideo?.admin_notes ?? '')) {
        updateData.admin_notes = adminNotes || null;
      }

      const { error } = await supabase
        .from('video_submissions')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Sync creator total rewards if reward amount changed
      if (editRewardAmount !== '' && selectedVideo) {
        const oldReward = selectedVideo.reward_amount || 0;
        const newReward = parseInt(editRewardAmount, 10) || 0;
        const diff = newReward - oldReward;

        if (diff !== 0 && selectedVideo.creator_id) {
          const { data: profile } = await supabase
            .from('creator_profiles')
            .select('total_rewards')
            .eq('id', selectedVideo.creator_id)
            .maybeSingle();

          if (profile) {
            await supabase
              .from('creator_profiles')
              .update({
                total_rewards: Math.max(0, (profile.total_rewards || 0) + diff),
                updated_at: new Date().toISOString(),
              })
              .eq('id', selectedVideo.creator_id);
          }
        }
      }

      await loadVideos();
      resetEditState();
      setToast({ message: '資料已更新', type: 'success' });
    } catch (error) {
      console.error('Error updating video data:', error);
      setToast({ message: '更新失敗，請重試', type: 'error' });
    } finally {
      setUpdating(null);
    }
  };

  const resetVideoStatus = async (id: string) => {
    setUpdating(id);
    try {
      const { error } = await supabase
        .from('video_submissions')
        .update({
          status: 'pending',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      await loadVideos();
      setSelectedVideo(null);
      setToast({ message: '已重設為待審核', type: 'success' });
    } catch (error) {
      console.error('Error resetting video status:', error);
      setToast({ message: '操作失敗', type: 'error' });
    } finally {
      setUpdating(null);
    }
  };

  // -------------------------------------------------------------------------
  // UI helpers
  // -------------------------------------------------------------------------
  const resetEditState = () => {
    setSelectedVideo(null);
    setAdminNotes('');
    setEditViewCount('');
    setEditRewardAmount('');
  };

  const searchFiltered = videos.filter(v => {
    const matchStatus = filter === 'all' || v.status === filter;
    const matchSearch =
      !searchQuery ||
      v.creator_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.video_title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  const stats = {
    total: videos.length,
    pending: videos.filter(v => v.status === 'pending').length,
    approved: videos.filter(v => v.status === 'approved').length,
    rejected: videos.filter(v => v.status === 'rejected').length,
    totalViews: videos.reduce((sum, v) => sum + v.view_count, 0),
    totalRewards: videos.reduce((sum, v) => sum + v.reward_amount, 0),
  };

  const getStatusStyle = (status: string) => {
    if (status === 'pending') return 'bg-amber-500/20 text-amber-300';
    if (status === 'approved') return 'bg-teal-500/20 text-teal-300';
    return 'bg-red-500/20 text-red-300';
  };

  const getStatusBorderStyle = (status: string) => {
    if (status === 'pending')
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    if (status === 'approved')
      return 'bg-teal-500/20 text-teal-300 border-teal-500/30';
    return 'bg-red-500/20 text-red-300 border-red-500/30';
  };

  const getStatusText = (status: string) => {
    if (status === 'pending') return '待審核';
    if (status === 'approved') return '已通過';
    return '已拒絕';
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, string> = {
      instagram: 'ri-instagram-line',
      tiktok: 'ri-tiktok-line',
      youtube: 'ri-youtube-line',
    };
    return icons[platform] || 'ri-video-line';
  };

  const openDetail = (video: VideoSubmission) => {
    setSelectedVideo(video);
    setAdminNotes(video.admin_notes || '');
    setEditViewCount(String(video.view_count));
    setEditRewardAmount(String(video.reward_amount));
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <AdminLayout title="影片審核">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="影片審核">
      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-fadeIn">
          <div
            className={`px-6 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2 ${
              toast.type === 'success' ? 'bg-teal-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            <i
              className={`${
                toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
              } text-lg`}
            ></i>
            {toast.message}
          </div>
        </div>
      )}

      {/* ==================== Desktop ==================== */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
          <StatCard
            icon="ri-video-line"
            bg="bg-teal-500/20"
            title="總影片數"
            value={stats.total}
          />
          <StatCard
            icon="ri-time-line"
            bg="bg-yellow-500/20"
            title="待審核"
            value={stats.pending}
          />
          <StatCard
            icon="ri-checkbox-circle-line"
            bg="bg-teal-500/20"
            title="已通過"
            value={stats.approved}
          />
          <StatCard
            icon="ri-close-circle-line"
            bg="bg-red-500/20"
            title="已拒絕"
            value={stats.rejected}
          />
          <StatCard
            icon="ri-eye-line"
            bg="bg-amber-500/20"
            title="總觀看數"
            value={stats.totalViews.toLocaleString()}
          />
          <StatCard
            icon="ri-money-dollar-circle-line"
            bg="bg-orange-500/20"
            title="總獎金"
            value={`NT${stats.totalRewards.toLocaleString()}`}
          />
        </div>

        {/* List */}
        <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">影片審核列表</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="搜尋創作者或影片標題..."
                  className="pl-9 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors w-64"
                />
              </div>
              <div className="flex items-center gap-1">
                {(['all', 'pending', 'approved', 'rejected'] as const).map(key => (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                      filter === key
                        ? 'bg-teal-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {key === 'all'
                      ? '全部'
                      : key === 'pending'
                      ? '待審核'
                      : key === 'approved'
                      ? '已通過'
                      : '已拒絕'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {searchFiltered.length === 0 ? (
            <div className="p-12 text-center">
              <i className="ri-inbox-line text-6xl text-slate-600 mb-4 block"></i>
              <p className="text-slate-400">
                {searchQuery ? '找不到符合的影片' : '暫無影片資料'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    {[
                      '影片標題',
                      '創作者',
                      '平台',
                      '觀看數',
                      '獎金',
                      '狀態',
                      '提交日期',
                      '操作',
                    ].map(col => (
                      <th
                        key={col}
                        className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {searchFiltered.map(video => (
                    <tr
                      key={video.id}
                      className="hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <a
                          href={video.video_url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-white hover:text-teal-400 font-medium"
                        >
                          {video.video_title}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">{video.creator_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <i
                            className={`${getPlatformIcon(video.platform)} text-xl text-teal-400 mr-2`}
                          ></i>
                          <span className="text-slate-300 text-sm capitalize">{video.platform}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">
                          {video.view_count.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-teal-400 font-medium text-sm">
                          NT${video.reward_amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBorderStyle(
                            video.status
                          )}`}
                        >
                          {getStatusText(video.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {format(new Date(video.created_at), 'yyyy/MM/dd HH:mm')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openDetail(video)}
                          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-medium whitespace-nowrap"
                        >
                          管理
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ==================== Mobile ==================== */}
      <div className="md:hidden">
        {/* Search & filter bar */}
        <div className="px-4 pt-3 pb-2 sticky top-[52px] z-30 bg-slate-950 space-y-2">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="搜尋創作者或影片..."
              className="w-full pl-8 pr-4 py-2 bg-slate-900 border border-slate-800/60 rounded-xl text-white text-xs placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {[
              { key: 'all' as const, label: '全部', count: stats.total },
              { key: 'pending' as const, label: '待審核', count: stats.pending },
              { key: 'approved' as const, label: '已通過', count: stats.approved },
              { key: 'rejected' as const, label: '已拒絕', count: stats.rejected },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  filter === tab.key
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-300'
                }`}
              >
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                    filter === tab.key ? 'bg-white/20' : 'bg-slate-700'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="px-4 pt-2">
          {searchFiltered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-800 rounded-full mb-3">
                <i className="ri-inbox-line text-2xl text-slate-600"></i>
              </div>
              <p className="text-slate-500 text-xs">
                {searchQuery ? '找不到符合的影片' : '暫無影片資料'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {searchFiltered.map(video => (
                <button
                  key={video.id}
                  onClick={() => openDetail(video)}
                  className="w-full text-left bg-slate-900 rounded-xl px-4 py-3 border border-slate-800/60 active:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
                        <i
                          className={`${getPlatformIcon(video.platform)} text-sm text-slate-400`}
                        ></i>
                      </div>
                      <span className="text-white text-sm font-medium truncate">
                        {video.video_title}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusStyle(
                        video.status
                      )}`}
                    >
                      {getStatusText(video.status)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 pl-[42px] text-[11px] text-slate-500">
                    <span className="truncate">{video.creator_name}</span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-eye-line text-[10px]"></i>
                      {video.view_count.toLocaleString()}
                    </span>
                    <span className="text-teal-400">NT${video.reward_amount.toLocaleString()}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ==================== Detail Modal ==================== */}
      {selectedVideo && (
        <>
          {/* Desktop modal */}
          <div
            className="hidden md:flex fixed inset-0 bg-black/70 items-center justify-center z-50 p-6"
            onClick={() => setSelectedVideo(null)}
          >
            <div
              className="bg-slate-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-700"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-8 py-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">影片管理</h2>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-700"
                >
                  <i className="ri-close-line text-2xl text-slate-400"></i>
                </button>
              </div>

              <div className="px-8 py-8 space-y-6">
                {/* Basic info */}
                <InfoRow label="影片標題" value={selectedVideo.video_title} />
                <InfoRow
                  label="影片連結"
                  value={selectedVideo.video_url}
                  isLink
                />
                <div className="grid md:grid-cols-2 gap-6">
                  <InfoRow label="創作者" value={selectedVideo.creator_name} />
                  <InfoRow
                    label="平台"
                    value={selectedVideo.platform}
                    icon={getPlatformIcon(selectedVideo.platform)}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <InfoRow label="遊戲類型" value={selectedVideo.game_type} />
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">審核狀態</label>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBorderStyle(
                          selectedVideo.status
                        )}`}
                      >
                        {getStatusText(selectedVideo.status)}
                      </span>
                      {selectedVideo.status !== 'pending' && (
                        <button
                          onClick={() => resetVideoStatus(selectedVideo.id)}
                          disabled={updating === selectedVideo.id}
                          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-amber-300 rounded text-xs font-medium disabled:cursor-not-allowed"
                        >
                          <i className="ri-refresh-line mr-1"></i>重設為待審核
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {selectedVideo.description && (
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">影片描述</label>
                    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-slate-300 whitespace-pre-wrap">{selectedVideo.description}</p>
                    </div>
                  </div>
                )}

                {/* Editable data */}
                <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-white font-bold mb-4 flex items-center">
                    <i className="ri-edit-line text-teal-400 mr-2"></i>數據管理
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <InputRow
                      label="觀看數"
                      value={editViewCount}
                      setValue={setEditViewCount}
                    />
                    <InputRow
                      label="獎金金額（NT$）"
                      value={editRewardAmount}
                      setValue={setEditRewardAmount}
                      step={100}
                    />
                  </div>
                </div>

                {/* Admin notes */}
                <div>
                  <label className="block text-slate-400 text-sm mb-2">管理員備註</label>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    maxLength={500}
                    placeholder="輸入審核備註..."
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 resize-none text-sm"
                    rows={3}
                  />
                </div>

                {/* Submit date */}
                <div>
                  <label className="block text-slate-400 text-sm mb-2">提交日期</label>
                  <p className="text-white">
                    {format(new Date(selectedVideo.created_at), 'yyyy/MM/dd HH:mm')}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-3 pt-6 border-t border-slate-700">
                  {selectedVideo.status !== 'pending' && (
                    <button
                      onClick={() => updateVideoData(selectedVideo.id)}
                      disabled={updating === selectedVideo.id}
                      className="w-full bg-slate-700 hover:bg-slate-600 disabled:bg-slate-600 text-white py-3 rounded-lg font-medium disabled:cursor-not-allowed"
                    >
                      <i className="ri-save-line mr-2"></i>儲存修改
                    </button>
                  )}
                  {selectedVideo.status === 'pending' && (
                    <div className="flex gap-4">
                      <button
                        onClick={() => updateVideoStatus(selectedVideo.id, 'approved', adminNotes)}
                        disabled={updating === selectedVideo.id}
                        className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white py-3 rounded-lg font-medium disabled:cursor-not-allowed"
                      >
                        <i className="ri-checkbox-circle-line mr-2"></i>通過審核
                      </button>
                      <button
                        onClick={() => updateVideoStatus(selectedVideo.id, 'rejected', adminNotes)}
                        disabled={updating === selectedVideo.id}
                        className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 text-white py-3 rounded-lg font-medium disabled:cursor-not-allowed"
                      >
                        <i className="ri-close-circle-line mr-2"></i>拒絕審核
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile full‑screen */}
          <div className="md:hidden fixed inset-0 z-50 bg-slate-950 flex flex-col animate-slideUp">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60">
              <button
                onClick={() => setSelectedVideo(null)}
                className="flex items-center gap-1 text-teal-400 text-sm"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>返回
              </button>
              <span className="text-white text-sm font-semibold">影片管理</span>
              <div className="w-14"></div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="mb-5">
                <h2 className="text-white font-semibold text-base mb-1">
                  {selectedVideo.video_title}
                </h2>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusStyle(
                      selectedVideo.status
                    )}`}
                  >
                    {getStatusText(selectedVideo.status)}
                  </span>
                  {selectedVideo.status !== 'pending' && (
                    <button
                      onClick={() => resetVideoStatus(selectedVideo.id)}
                      disabled={updating === selectedVideo.id}
                      className="px-2 py-0.5 bg-slate-800 text-amber-300 rounded-full text-[10px] font-medium disabled:opacity-50"
                    >
                      <i className="ri-refresh-line mr-0.5"></i>重設
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-slate-900 rounded-xl border border-slate-800/60 divide-y divide-slate-800/60">
                  <MobileInfoRow icon="ri-user-line" label="創作者" value={selectedVideo.creator_name} />
                  <MobileInfoRow icon={getPlatformIcon(selectedVideo.platform)} label="平台" value={selectedVideo.platform} />
                  <MobileInfoRow icon="ri-gamepad-line" label="遊戲類型" value={selectedVideo.game_type} />
                  <MobileInfoRow
                    icon="ri-calendar-line"
                    label="提交日期"
                    value={format(new Date(selectedVideo.created_at), 'yyyy/MM/dd HH:mm')}
                  />
                </div>

                <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-3.5">
                  <p className="text-slate-500 text-[11px] mb-1.5">影片連結</p>
                  <a
                    href={selectedVideo.video_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-teal-400 text-xs break-all"
                  >
                    {selectedVideo.video_url}
                  </a>
                </div>

                {selectedVideo.description && (
                  <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-3.5">
                    <p className="text-slate-500 text-[11px] mb-1.5">影片描述</p>
                    <p className="text-slate-300 text-xs whitespace-pre-wrap">{selectedVideo.description}</p>
                  </div>
                )}

                {/* Editable data */}
                <div className="bg-slate-900 rounded-xl border border-teal-500/20 p-3.5">
                  <p className="text-teal-400 text-xs font-medium mb-3 flex items-center">
                    <i className="ri-edit-line mr-1.5"></i>數據管理
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-slate-500 text-[11px] mb-1">觀看數</label>
                      <input
                        type="number"
                        value={editViewCount}
                        onChange={e => setEditViewCount(e.target.value)}
                        min="0"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-500 text-[11px] mb-1">獎金金額（NT$）</label>
                      <input
                        type="number"
                        value={editRewardAmount}
                        onChange={e => setEditRewardAmount(e.target.value)}
                        min="0"
                        step="100"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-3.5">
                  <p className="text-slate-500 text-[11px] mb-1.5">管理員備註</p>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    maxLength={500}
                    placeholder="輸入審核備註..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-teal-500 resize-none"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-800/60 space-y-2 pb-[calc(12px+env(safe-area-inset-bottom))]">
              {selectedVideo.status !== 'pending' && (
                <button
                  onClick={() => updateVideoData(selectedVideo.id)}
                  disabled={updating === selectedVideo.id}
                  className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-1.5"></i>儲存修改
                </button>
              )}
              {selectedVideo.status === 'pending' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => updateVideoStatus(selectedVideo.id, 'rejected', adminNotes)}
                    disabled={updating === selectedVideo.id}
                    className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-red-400 rounded-xl text-sm font-medium disabled:cursor-not-allowed"
                  >
                    拒絕
                  </button>
                  <button
                    onClick={() => updateVideoStatus(selectedVideo.id, 'approved', adminNotes)}
                    disabled={updating === selectedVideo.id}
                    className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium disabled:cursor-not-allowed"
                  >
                    通過
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Inline styles for custom animations */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.25s ease-out; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </AdminLayout>
  );
}

/* -------------------------------------------------------------------------
   Reusable UI components
-------------------------------------------------------------------------- */
function StatCard({
  icon,
  bg,
  title,
  value,
}: {
  icon: string;
  bg: string;
  title: string;
  value: string | number;
}) {
  return (
    <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
      <div className={`w-12 h-12 flex items-center justify-center ${bg} rounded-lg mb-4`}>
        <i className={`${icon} text-2xl text-teal-400`}></i>
      </div>
      <p className="text-slate-400 text-sm mb-1">{title}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function InfoRow({
  label,
  value,
  isLink,
  icon,
}: {
  label: string;
  value: string;
  isLink?: boolean;
  icon?: string;
}) {
  return (
    <div>
      <label className="block text-slate-400 text-sm mb-2">{label}</label>
      {isLink ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-teal-400 hover:text-teal-300 break-all"
        >
          {value}
        </a>
      ) : (
        <p className="text-white">{value}</p>
      )}
    </div>
  );
}

function InputRow({
  label,
  value,
  setValue,
  step = 1,
}: {
  label: string;
  value: string;
  setValue: (v: string) => void;
  step?: number;
}) {
  return (
    <div>
      <label className="block text-slate-400 text-sm mb-2">{label}</label>
      <input
        type="number"
        value={value}
        onChange={e => setValue(e.target.value)}
        min="0"
        step={step}
        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
        placeholder={label}
      />
    </div>
  );
}

function MobileInfoRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between px-3.5 py-3">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-sm text-slate-500`}></i>
        </div>
        <span className="text-slate-500 text-xs">{label}</span>
      </div>
      <span className="text-white text-xs font-medium max-w-[60%] truncate">{value}</span>
    </div>
  );
}
